import { useTranslation } from 'react-i18next';
import { Badge, cn } from '@kognitos/lattice';
import { TRIGGER_STATUS, type TTriggerStatus } from './TriggerType';

/**
 * Props for the TriggerStatusBadge component
 */
export interface ITriggerStatusBadgeProps {
  /** Current status of the trigger */
  status: TTriggerStatus;
  /** Optional className for custom styling */
  className?: string;
  /** Whether to use a minimal badge */
  minimal?: boolean;
}

/**
 * TriggerStatusBadge Component
 *
 * Displays a colored badge indicating the current status of a trigger.
 * - Active: Green badge
 * - Error: Red badge
 * - Pending: Yellow badge
 *
 * @param props - Component props
 * @returns Badge component with appropriate styling based on status
 */
export function TriggerStatusBadge({
  status,
  className,
  minimal = false,
}: ITriggerStatusBadgeProps) {
  const { t } = useTranslation('automation-inputs');

  return (
    <Badge
      variant="secondary"
      className={cn(
        {
          'text-success bg-success/5': status === TRIGGER_STATUS.ACTIVE,
          'text-destructive bg-destructive/5':
            status === TRIGGER_STATUS.ERROR || status === TRIGGER_STATUS.PAUSED,
          'text-warning bg-warning/5': status === TRIGGER_STATUS.PENDING,
        },
        {
          'bg-transparent p-0': minimal,
        },
        className,
      )}
    >
      {t(`triggerTypes.status.${status}`)}
    </Badge>
  );
}
